"use client"

/**
 * ConfigureViewDrawer — Strapi-style "Configure the view" panel.
 *
 * Lets an editor pick which columns show + their order (drag to reorder),
 * entries-per-page, default sort attribute + direction, and toggle the
 * search / filters / bulk blocks.
 *
 * Edits are staged in a local *draft* and only committed when the user clicks
 * "Lưu" — so opening/closing the drawer never touches the live list. Closing
 * with unsaved changes asks for confirmation. "Đặt lại" restores the module
 * defaults into the draft (still requires Save to persist).
 *
 * Antd throughout. The Drawer portals to <body>, outside `.admin-shell`, so we
 * set `rootClassName="admin-shell"` to keep the OKLch design tokens resolving.
 */

import { HolderOutlined, LockOutlined, ReloadOutlined } from "@ant-design/icons"
import { App, Button, Checkbox, Divider, Drawer, Select, Space, Switch, Typography } from "antd"
import { useEffect, useMemo, useState } from "react"
import { ReactSortable } from "react-sortablejs"
import { t } from "@/languages/i18n"
import type { ColumnMeta, ViewConfig } from "./types"
import { viewConfigEqual } from "./use-view-config"

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export interface ConfigureViewFeatures {
  /** Show the "search" toggle. */
  search?: boolean
  /** Show the "filters" toggle. */
  filters?: boolean
  /** Show the "bulk actions" toggle. */
  bulk?: boolean
}

interface ConfigureViewDrawerProps {
  open: boolean
  onClose: () => void
  registry: ColumnMeta[]
  /** The currently-saved config. */
  config: ViewConfig
  /** Module defaults — restored by the Reset button. */
  defaultConfig: ViewConfig
  /** Commit the edited draft (persist to localStorage). */
  onSave: (next: ViewConfig) => void
  /** Which block toggles are meaningful for this module. Defaults to all on. */
  features?: ConfigureViewFeatures
  pageSizeOptions?: number[]
  /** Drawer title override. */
  title?: string
}

type OrderItem = { id: string; title: string; alwaysOn: boolean }

const labelStyle = { color: "var(--admin-foreground)", fontSize: 13, fontWeight: 500 } as const
const hintStyle = { color: "var(--admin-muted-fg)", fontSize: 12 } as const

const toOrderItems = (order: string[], metaByKey: Map<string, ColumnMeta>): OrderItem[] =>
  order
    .map(key => metaByKey.get(key))
    .filter((m): m is ColumnMeta => Boolean(m))
    .map(m => ({ id: m.key, title: m.title, alwaysOn: Boolean(m.alwaysOn) }))

export function ConfigureViewDrawer({
  open,
  onClose,
  registry,
  config,
  defaultConfig,
  onSave,
  features,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  title,
}: ConfigureViewDrawerProps) {
  const { modal } = App.useApp()
  const metaByKey = useMemo(() => new Map(registry.map(m => [m.key, m])), [registry])

  // Local draft — the live config is only touched on Save.
  const [draft, setDraft] = useState<ViewConfig>(config)
  const [orderItems, setOrderItems] = useState<OrderItem[]>(() => toOrderItems(config.order, metaByKey))

  // Re-seed the draft from the saved config every time the drawer opens.
  useEffect(() => {
    if (open) {
      setDraft(config)
      setOrderItems(toOrderItems(config.order, metaByKey))
    }
    // Only react to open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Keep the drag list in sync whenever the draft order changes (e.g. Reset).
  useEffect(() => {
    setOrderItems(toOrderItems(draft.order, metaByKey))
  }, [draft.order, metaByKey])

  const dirty = !viewConfigEqual(draft, config)
  const visibleSet = useMemo(() => new Set(draft.visibleKeys), [draft.visibleKeys])
  const sortableColumns = useMemo(() => registry.filter(m => m.sortable), [registry])

  const showSearch = features?.search ?? true
  const showFilters = features?.filters ?? true
  const showBulk = features?.bulk ?? true

  const patch = (partial: Partial<ViewConfig>) => setDraft(prev => ({ ...prev, ...partial }))

  const toggleColumn = (key: string, checked: boolean) => {
    const meta = metaByKey.get(key)
    if (meta?.alwaysOn) return
    const nextSet = new Set(draft.visibleKeys)
    if (checked) nextSet.add(key)
    else nextSet.delete(key)
    patch({ visibleKeys: draft.order.filter(k => nextSet.has(k)) })
  }

  const handleReorder = (items: OrderItem[]) => {
    // ReactSortable fires setList on mount / re-render with the same list.
    // Only react when the order actually changed — otherwise we thrash state
    // and risk a render loop (see inclusion-form.tsx for the same guard).
    const changed = items.length !== orderItems.length || items.some((it, i) => it.id !== orderItems[i]?.id)
    if (!changed) return
    setOrderItems(items)
    const nextOrder = items.map(i => i.id)
    setDraft(prev => ({
      ...prev,
      order: nextOrder,
      visibleKeys: nextOrder.filter(k => new Set(prev.visibleKeys).has(k)),
    }))
  }

  const handleSave = () => {
    onSave(draft)
    onClose()
  }

  const handleReset = () => setDraft(defaultConfig)

  const handleClose = () => {
    if (!dirty) {
      onClose()
      return
    }
    modal.confirm({
      title: t("Thay đổi chưa được lưu"),
      content: t("Bạn có thay đổi chưa lưu. Thoát mà không lưu?"),
      okText: t("Thoát không lưu"),
      cancelText: t("Ở lại"),
      okButtonProps: { danger: true },
      onOk: onClose,
    })
  }

  return (
    <Drawer
      title={title ?? t("Cấu hình hiển thị")}
      placement="right"
      width={380}
      open={open}
      onClose={handleClose}
      rootClassName="admin-shell"
      maskClosable={false}
      styles={{ body: { paddingTop: 12 } }}
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={handleReset}>
          {t("Đặt lại")}
        </Button>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button onClick={handleClose}>{t("Huỷ")}</Button>
          <Button type="primary" disabled={!dirty} onClick={handleSave}>
            {t("Lưu")}
          </Button>
        </div>
      }
    >
      {/* Columns: visibility + drag-to-reorder */}
      <Typography.Text style={labelStyle}>{t("Cột hiển thị")}</Typography.Text>
      <div style={hintStyle} className="mb-2">
        {t("Kéo để đổi thứ tự, bỏ chọn để ẩn cột.")}
      </div>
      <ReactSortable
        list={orderItems}
        setList={handleReorder}
        handle=".vc-drag-handle"
        animation={150}
        className="flex flex-col"
      >
        {orderItems.map(item => (
          <div
            key={item.id}
            className="flex items-center gap-2 py-1.5 px-1 rounded"
            style={{ borderBottom: "1px solid var(--admin-border)" }}
          >
            <HolderOutlined
              className="vc-drag-handle cursor-grab active:cursor-grabbing"
              style={{ color: "var(--admin-muted-fg)" }}
            />
            <Checkbox
              checked={item.alwaysOn || visibleSet.has(item.id)}
              disabled={item.alwaysOn}
              onChange={e => toggleColumn(item.id, e.target.checked)}
            >
              <span style={{ color: "var(--admin-foreground)" }}>{item.title}</span>
            </Checkbox>
            {item.alwaysOn && (
              <LockOutlined style={{ color: "var(--admin-muted-fg)", fontSize: 12, marginLeft: "auto" }} />
            )}
          </div>
        ))}
      </ReactSortable>

      <Divider style={{ margin: "16px 0" }} />

      {/* Entries per page */}
      <div className="flex items-center justify-between mb-3">
        <Typography.Text style={labelStyle}>{t("Số dòng mỗi trang")}</Typography.Text>
        <Select
          size="small"
          style={{ width: 100 }}
          value={draft.pageSize}
          onChange={value => patch({ pageSize: value })}
          options={pageSizeOptions.map(n => ({ value: n, label: String(n) }))}
        />
      </div>

      {/* Default sort */}
      <div className="flex items-center justify-between mb-3">
        <Typography.Text style={labelStyle}>{t("Sắp xếp mặc định")}</Typography.Text>
        <Space size={4}>
          <Select
            size="small"
            style={{ width: 130 }}
            value={draft.sortField ?? ""}
            onChange={value => patch({ sortField: value || undefined })}
            options={[
              { value: "", label: t("Không") },
              ...sortableColumns.map(c => ({ value: c.key, label: c.title })),
            ]}
          />
          <Select
            size="small"
            style={{ width: 90 }}
            disabled={!draft.sortField}
            value={draft.sortOrder ?? "desc"}
            onChange={value => patch({ sortOrder: value })}
            options={[
              { value: "asc", label: t("Tăng dần") },
              { value: "desc", label: t("Giảm dần") },
            ]}
          />
        </Space>
      </div>

      {(showSearch || showFilters || showBulk) && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Typography.Text style={labelStyle}>{t("Khối giao diện")}</Typography.Text>
          <div className="mt-2 flex flex-col gap-2">
            {showSearch && (
              <div className="flex items-center justify-between">
                <span style={{ color: "var(--admin-foreground)" }}>{t("Tìm kiếm")}</span>
                <Switch
                  size="small"
                  checked={draft.enableSearch}
                  onChange={checked => patch({ enableSearch: checked })}
                />
              </div>
            )}
            {showFilters && (
              <div className="flex items-center justify-between">
                <span style={{ color: "var(--admin-foreground)" }}>{t("Bộ lọc")}</span>
                <Switch
                  size="small"
                  checked={draft.enableFilters}
                  onChange={checked => patch({ enableFilters: checked })}
                />
              </div>
            )}
            {showBulk && (
              <div className="flex items-center justify-between">
                <span style={{ color: "var(--admin-foreground)" }}>{t("Thao tác hàng loạt")}</span>
                <Switch
                  size="small"
                  checked={draft.enableBulk}
                  onChange={checked => patch({ enableBulk: checked })}
                />
              </div>
            )}
          </div>
        </>
      )}
    </Drawer>
  )
}

export default ConfigureViewDrawer
